import React from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import PropTypes from 'prop-types'
import { Box, Card, Chip, colors, IconButton } from '@material-ui/core'
import { ChevronRight as ChevronRightIcon } from 'react-feather'
import PerfectScrollbar from 'react-perfect-scrollbar'

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from 'components/Table'

import { routes } from 'Routes'
import helpers from 'helpers'
import constants from 'constants/index'

const ActivitiesTable = ({
  questionnaires,
  governanceProject,
  currentTab,
  searchQuery = '',
  requestIsLoading = false,
}) => {
  const history = useHistory()

  const underContestation = governanceProject?.underContestation
  const underContestationAudit = governanceProject?.underContestationAudit

  const {
    ACTIVITY_STATUS_LABELS,
    REVIEW_ACTIVITY_STATUS_LABELS,
    WITHOUT_CODE_LABEL,
  } = constants.governanceProjectActivity

  // Abas do projeto; rótulo e cor de cada controle seguem o questionário dele.
  const isCoveredByReview = !!governanceProject?.coveredByReview

  const allActivities = questionnaires?.flatMap((questionnaire) =>
    (questionnaire?.governanceProjectActivities || []).map((activity) => ({
      ...activity,
      questionnaire,
      questionnaireName: questionnaire?.kind || '',
    })),
  )

  const filtered = allActivities?.filter((activity) => {
    const activityStatus =
      underContestation || (underContestationAudit && activity?.contestation)
        ? activity?.contestationStatus
        : activity?.status
    const matchesTab = helpers.governanceProject.isActivityInBoardTab({
      activityStatus,
      currentTab,
      isCoveredByReview,
    })
    const query = searchQuery.toLowerCase()
    const matchesSearch =
      !searchQuery ||
      activity?.theme?.toLowerCase().includes(query) ||
      activity?.code?.toLowerCase().includes(query) ||
      activity?.questionnaireName?.toLowerCase().includes(query)
    return matchesTab && matchesSearch
  })

  const sorted = filtered?.slice().sort((a, b) => {
    const codeCompare = (a.code || '').localeCompare(b.code || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
    if (codeCompare !== 0) return codeCompare
    return (a.theme || '').localeCompare(b.theme || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  })

  const getActivityStatus = (activity) =>
    underContestation || (underContestationAudit && activity?.contestation)
      ? activity?.contestationStatus
      : activity?.status

  const handleNavigate = (activity) => {
    history.push(
      reverse(routes.governanceProjects.activity.manage, {
        governanceProjectId: governanceProject?.id,
        activityId: activity?.id,
      }),
    )
  }

  return (
    <Box mt={1}>
      <Card>
        <PerfectScrollbar
          options={{ wheelPropagation: true, useBothWheelAxes: true }}
        >
          <Table
            emptyMessage="Nenhuma atividade foi encontrada."
            isLoading={requestIsLoading}
          >
            <TableHead>
              <TableRow>
                <TableCell width="33%">Atividade</TableCell>
                <TableCell width="24%">Questionário</TableCell>
                <TableCell width="14%">Status</TableCell>
                <TableCell width="10%">Evidências</TableCell>
                <TableCell width="14%">Prazo</TableCell>
                <TableCell width="5%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted?.map((activity) => {
                const activityStatus = getActivityStatus(activity)
                const isQuestionnaireCoveredByReview =
                  !!activity?.questionnaire?.coveredByReview
                const statusLabels = isQuestionnaireCoveredByReview
                  ? REVIEW_ACTIVITY_STATUS_LABELS
                  : ACTIVITY_STATUS_LABELS
                const { statusColors } =
                  helpers.governanceProject.getStatusBoardPalette({
                    isUnderContestation:
                      underContestation || underContestationAudit,
                    isCoveredByReview: isQuestionnaireCoveredByReview,
                  })
                const chipColor =
                  statusColors[activityStatus] || colors.grey[500]

                return (
                  <TableRow
                    key={activity?.id}
                    onClick={() => handleNavigate(activity)}
                    pointer="true"
                  >
                    <TableCell>
                      {`[${activity?.code || WITHOUT_CODE_LABEL}] ${
                        activity?.theme || 'Não definido'
                      }`}
                    </TableCell>
                    <TableCell>
                      {`[${
                        activity?.questionnaire?.code || WITHOUT_CODE_LABEL
                      }] ${activity?.questionnaire?.kind || ''}`}
                    </TableCell>
                    <TableCell disableTooltip>
                      <Chip
                        size="small"
                        label={
                          statusLabels[activityStatus] || activityStatus || '-'
                        }
                        style={{
                          backgroundColor: chipColor,
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {`${activity?.evidences?.completed ?? '-'}/${
                        activity?.evidences?.total ?? '-'
                      }`}
                    </TableCell>
                    <TableCell>
                      {helpers.formatters.date(activity?.deadlineDate) || '-'}
                    </TableCell>
                    <TableCell disableTooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleNavigate(activity)}
                      >
                        <ChevronRightIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </Card>
    </Box>
  )
}

ActivitiesTable.propTypes = {
  questionnaires: PropTypes.arrayOf(PropTypes.object),
  governanceProject: PropTypes.object,
  currentTab: PropTypes.string.isRequired,
  searchQuery: PropTypes.string,
  requestIsLoading: PropTypes.bool,
}

export default ActivitiesTable
